import { yupResolver } from "@hookform/resolvers/yup";
import FormInput from "components/Common/Form/FormInput";
import FormSelect from "components/Common/Form/FormSelect";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { getPhoneValidation } from "constants/validate";
import { getTimeSlot } from "helpers/utils";
import {
  useGetRunnerCoordinationDefault,
  useListLocality,
  useListRunnerHub,
} from "hooks";
import { useListHolidayWithTime } from "hooks/holiday/useHoliday";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SingleDatePicker } from "react-dates";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useToasts } from "react-toast-notifications";
import {
  Button,
  Col,
  FormGroup,
  Label,
  ModalBody,
  ModalFooter,
  Row,
} from "reactstrap";
import * as yup from "yup";
import "./OrderCoordinationForm.scss";

/**
 * @typedef {{
 *  action_date: string | null;
 *  timeslot_id: Record<string, any> | null;
 *  runner_id: Record<string, any> | null;
 *  note: string;
 *  isResetWard: boolean;
 *  customerContactName: string;
 *  customerContactPhone: string;
 *  customerAddress: string;
 *  customerWardCode: DefaultObjectSelect | null;
 *  customerDistrictCode: DefaultObjectSelect | null;
 *  customerProvinceCode: DefaultObjectSelect | null;
 * }} FormInputs
 */

/**
 * @param {{
 *  defaultData: {timeslot_id:any,runner_id:any,full_address:any,ward_code:any,district_code:any,province_code:any,contact_name:any,contact_phone:any,note:any,is_new_address:any};
 *  timeSlots: Record<string, any>[];
 *  runners: Record<string, any>[];
 *  handleCancel: (val?: any) => void;
 *  onActionDateChange: (val?: any) => void;
 *  onTimeSlotChange: (val?: any) => void;
 *  onForwardOrder: (val?: any) => void;
 *  hubId:any;
 *  isAddressLevel2:boolean;
 *  isMultipleOrders:boolean;
 * }} props
 */
const OrderCoordinationForm = props => {
  const { t } = useTranslation(["order"]);
  const { addToast } = useToasts();

  const isFirstTimeSlotChange = useRef(true);

  const {
    defaultData,
    timeSlots,
    runners,
    handleCancel,
    onActionDateChange,
    onTimeSlotChange,
    onForwardOrder,
    hubId,
    isAddressLevel2: propIsAddressLevel2,
    isMultipleOrders,
  } = props;

  const USE_TIME_SLOT_RULE = false;

  const isEmptyCustomerInfo = useMemo(() => {
    if (!defaultData) return false;
    return (
      !defaultData.contact_name &&
      !defaultData.contact_phone &&
      !defaultData.full_address
    );
  }, [defaultData]);

  const [isRequireCustomerInfo, setIsRequireCustomerInfo] = useState(
    !isEmptyCustomerInfo && !isMultipleOrders
  );

  useEffect(() => {
    if (isEmptyCustomerInfo || isMultipleOrders) {
      setIsRequireCustomerInfo(false);
    }
  }, [isEmptyCustomerInfo, isMultipleOrders]);

  const [addressMappingState, setAddressMappingState] = useState({
    isProvinceSet: false,
    isDistrictSet: false,
    isWardSet: false,
  });

  // Date picker states
  const [datePickerState, setDatePickerState] = useState({
    actionDate: moment().startOf("day"),
    focusedInput: false,
  });

  // START GET holidays - Move this before effects object
  const [holidaysParams, setHolidaysParams] = useState({
    params: {
      columnFilter: {
        "filter[hub_id]": undefined,
      },
      sizePerPage: 10,
      page: -1,
    },
    run: false,
    useToDisalbeCalendar: true,
    queryKey: "holiday-list-order-coordination-with-time",
  });

  const {
    data: holidays,
    isFetching: isLoadingList,
    refetch: fetchHolidays,
    remove: removeHolidays,
  } = useListHolidayWithTime(holidaysParams);

  // Determine address level based on is_new_address or prop
  const isAddressLevel2 = useMemo(() => {
    if (defaultData?.is_new_address !== undefined) {
      return defaultData?.is_new_address === "N";
    }
    return propIsAddressLevel2;
  }, [defaultData?.is_new_address, propIsAddressLevel2]);

  /** @type {FormInputs} */
  const initDefaultValues = {
    action_date: null,
    timeslot_id: null,
    runner_id: null,
    note: "",

    isResetWard: false,

    customerContactName: "",
    customerContactPhone: "",
    customerAddress: "",
    customerWardCode: null,
    customerDistrictCode: null,
    customerProvinceCode: null,
  };

  const schemaDefault = useMemo(
    () => ({
      action_date: yup
        .string()
        .nullable()
        .required(t("form.validate.select", { title: t("form.action_date") })),
      timeslot_id: yup
        .object()
        .nullable()
        .required(t("form.validate.select", { title: t("form.timeslot_id") })),
      runner_id: yup
        .object()
        .nullable()
        .required(t("form.validate.select", { title: t("form.runner_id") })),
      note: yup.string(),

      isResetWard: yup.bool(),

      customerContactName: yup.string().nullable(),
      customerContactPhone: yup
        .string()
        .nullable()
        .matches(
          getPhoneValidation(t).regex,
          getPhoneValidation(t).errorMessage
        )
        .when(getPhoneValidation(t).when),
      customerAddress: yup.string().nullable(),
      customerWardCode: yup.object().nullable(),
      // .required(
      //   t("form.validate.select", { title: t("form.receiver_ward_code") })
      // ),
      customerDistrictCode: yup.object().nullable(),
      // .required(
      //   t("form.validate.select", { title: t("form.receiver_district_code") })
      // ),
      customerProvinceCode: yup.object().nullable(),
      // .required(
      //   t("form.validate.select", { title: t("form.receiver_province_code") })
      // ),
    }),
    []
  );

  const schemaCustomer = useMemo(() => {
    if (isRequireCustomerInfo) {
      return {
        customerContactName: yup.string().required(
          t("form.validate.required", {
            title: t("form.receiver_contact_name"),
          })
        ),
        customerContactPhone: yup
          .string()
          .required(
            t("form.validate.required", {
              title: t("form.receiver_contact_phone"),
            })
          )
          .matches(
            getPhoneValidation(t).regex,
            getPhoneValidation(t).errorMessage
          )
          .when(getPhoneValidation(t).when),
        customerAddress: yup.string().required(
          t("form.validate.required", {
            title: t("form.receiver_address"),
          })
        ),
        customerWardCode: yup
          .object()
          .nullable()
          .required(
            t("form.validate.select", {
              title: t("form.receiver_ward_code"),
            })
          ),
        customerDistrictCode: !isAddressLevel2
          ? yup
              .object()
              .nullable()
              .required(
                t("form.validate.select", {
                  title: t("form.receiver_district_code"),
                })
              )
          : yup.object().nullable(),
        customerProvinceCode: yup
          .object()
          .nullable()
          .required(
            t("form.validate.select", {
              title: t("form.receiver_province_code"),
            })
          ),
      };
    }

    return {};
  }, [isRequireCustomerInfo, isAddressLevel2]);

  const schema = useMemo(() => {
    return yup.object({ ...schemaDefault, ...schemaCustomer });
  }, [schemaDefault, schemaCustomer]);

  const mainForm = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: { ...initDefaultValues },
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
    watch,
    trigger,
  } = mainForm;

  // Watch form fields for changes
  const [
    watchTimeSlotId,
    watchActionDate,
    watchIsResetWard,
    watchCustomerProvinceCode,
    watchCustomerDistrictCode,
    watchCustomerWardCode,
  ] = watch([
    "timeslot_id",
    "action_date",
    "isResetWard",
    "customerProvinceCode",
    "customerDistrictCode",
    "customerWardCode",
  ]);

  // Default form states
  const [defaultStates, setDefaultStates] = useState({
    isRunnerDefaultSet: false,
    isTimeSlotDefaultSet: false,
  });

  // API calls object
  const apiCalls = {
    listLocality: {
      province: useListLocality({
        onSuccess: response => {},
      }),
      district: useListLocality({
        onSuccess: response => {},
      }),
      ward: useListLocality({
        onSuccess: response => {},
      }),
    },
    runners: useListRunnerHub({
      hub_id: hubId,
    }),
    runnerCoordinationDefault: useGetRunnerCoordinationDefault(
      {
        hub_id: hubId,
        time_slot_id: watchTimeSlotId?.value?.id,
        province_code: watchCustomerProvinceCode?.value,
        district_code: isAddressLevel2
          ? watchCustomerWardCode?.value
          : watchCustomerWardCode?.value,
        ward_code: isAddressLevel2 ? null : watchCustomerDistrictCode?.value,
        action_date: watchActionDate,
      },
      response => {
        const { data, result, message } = response;
        if (result) {
          setValue(
            "runner_id",
            {
              label: data?.display_name,
              value: data,
            },
            {
              shouldValidate: true,
            }
          );
        } else {
          setValue("runner_id", null, { shouldValidate: true });

          if (typeof message === "string") {
            addToast(message, {
              appearance: "warning",
              autoDismiss: true,
            });
          } else {
            const fields = Object.keys(message);
            const errorMessage = fields.reduce((acc, field) => {
              // if is last field, not add dot to end of string
              if (field === fields[fields.length - 1]) {
                return acc + message?.[field]?.[0];
              }
              return acc + message?.[field]?.[0] + ", ";
            }, " ");
            addToast(errorMessage, {
              appearance: "warning",
              autoDismiss: true,
            });
          }
        }
      }
    ),
  };

  const formatLocalityList = data => {
    if (!data) {
      return [];
    }
    /** @type {DefaultObjectSelect[]} */
    let items = [];

    if (data && typeof data === "object") {
      items = Object.entries(data).map(([key, value]) => ({
        label: value,
        value: key,
      }));
    }
    return items || [];
  };

  const provinceList = useMemo(() => {
    const data = apiCalls?.listLocality?.province?.data?.data;
    return formatLocalityList(data) || [];
  }, [apiCalls?.listLocality?.province?.data]);

  const districtList = useMemo(() => {
    const data = apiCalls?.listLocality?.district?.data?.data;
    return formatLocalityList(data) || [];
  }, [apiCalls?.listLocality?.district?.data]);

  const wardList = useMemo(() => {
    const data = apiCalls?.listLocality?.ward?.data?.data;
    return formatLocalityList(data) || [];
  }, [apiCalls?.listLocality?.ward?.data]);

  // Handlers object
  const handlers = {
    changeTimeSlot: item => {
      setValue("timeslot_id", item, { shouldValidate: true });
      onTimeSlotChange(item?.value?.id);
    },
    changeRunner: runner => {
      setValue("runner_id", runner, { shouldValidate: true });
    },
    changeDatePicker: date => {
      setValue("action_date", moment(date).format("YYYY-MM-DD"), {
        shouldValidate: true,
      });
      setDatePickerState(prev => ({ ...prev, actionDate: date }));
      onActionDateChange(moment(date).format("YYYY-MM-DD"));
    },
    toggleFocusedInput: ({ focused }) => {
      setDatePickerState(prev => ({ ...prev, focusedInput: focused }));
    },
    submit: () => {
      const data = getValues();
      // Logic dựa vào isAddressLevel2: 2 cấp hay 3 cấp
      const customer_district_code = isAddressLevel2
        ? data?.customerWardCode?.value // 2 cấp: ward_code được sử dụng làm district_code
        : data?.customerDistrictCode?.value; // 3 cấp: district_code thật
      const customer_ward_code = isAddressLevel2
        ? null // 2 cấp: ward_code = null
        : data?.customerWardCode?.value; // 3 cấp: ward_code có giá trị

      const forwardOrder = {
        order_id: null,
        transport_id: null,
        runner_id: data?.runner_id?.value?.id,
        bus_id: null,
        shipping_type: 1,
        note: data?.note,
        action_date: moment(data?.action_date).format("YYYY-MM-DD"),
        timeslot_id: data?.timeslot_id?.value?.id,
        customer_contact_name: data.customerContactName,
        customer_contact_phone: data.customerContactPhone,
        customer_address: data.customerAddress,
        customer_ward_code: customer_ward_code,
        customer_district_code: customer_district_code,
        customer_province_code: data.customerProvinceCode?.value || null,
      };
      handleCancel();
      onForwardOrder(forwardOrder);
    },
  };

  // Effects functions
  const effects = {
    initializeForm: () => {
      setValue("action_date", moment().format("YYYY-MM-DD"));
      // Call API get province list immediately
      apiCalls.listLocality.province.mutate({
        countryId: 1,
        type: 1,
        parentCode: "",
        record_status: isAddressLevel2 ? "O" : "C",
      });

      apiCalls.runners.refetch();
    },
    handleTimeSlotChange: () => {
      if (watchTimeSlotId?.value?.id) {
        if (!isFirstTimeSlotChange.current) {
          // Only run when not first render
          apiCalls.runnerCoordinationDefault.refetch();
        } else {
          isFirstTimeSlotChange.current = false;
        }
      }
    },
    handleHubChange: () => {
      setHolidaysParams(prev => ({
        ...prev,
        params: {
          ...prev.params,
          columnFilter: {
            ...prev.params.columnFilter,
            "filter[hub_id]": hubId || undefined,
          },
        },
        run: true,
      }));
    },
    handleCustomerInfoChange: () => {
      if (isMultipleOrders) return;

      const data = getValues();
      const hasCustomerInfo = !!(
        data?.customerContactName ||
        data?.customerContactPhone ||
        data?.customerAddress ||
        data?.customerWardCode
      );
      setIsRequireCustomerInfo(hasCustomerInfo);
    },
    handleWardReset: () => {
      if (watchIsResetWard) {
        setValue("customerWardCode", initDefaultValues.customerWardCode);
        if (!isAddressLevel2) {
          setValue(
            "customerDistrictCode",
            initDefaultValues.customerDistrictCode
          );
        }

        if (watchCustomerProvinceCode) {
          if (isAddressLevel2) {
            // 2 cấp: Fetch districts từ province (record-status = O, type = 3)
            apiCalls.listLocality.ward.mutate({
              countryId: 1,
              type: 2,
              parentCode: watchCustomerProvinceCode.value,
              record_status: "O", // 2 cấp dùng record-status = O
            });
          } else {
            // 3 cấp: Fetch districts trước (record-status = C)
            apiCalls.listLocality.district.mutate({
              countryId: 1,
              type: 2,
              parentCode: watchCustomerProvinceCode.value,
              record_status: "C",
            });
          }
        }

        setValue("isResetWard", false);
      }
    },
  };

  // Setup effects
  useEffect(effects.initializeForm, []);

  // Handle timeslot change effect
  useEffect(() => {
    effects.handleTimeSlotChange();
  }, [watchTimeSlotId?.value?.id]);

  const isFirstProvinceChange = useRef(true);
  useEffect(() => {
    if (watchCustomerProvinceCode?.value) {
      if (!isFirstProvinceChange.current) {
        apiCalls.runnerCoordinationDefault.refetch();
      } else {
        isFirstProvinceChange.current = false;
      }
    }
  }, [watchCustomerProvinceCode]);

  const isFirstDistrictChange = useRef(true);
  useEffect(() => {
    if (watchCustomerDistrictCode?.value) {
      if (!isFirstDistrictChange.current) {
        apiCalls.runnerCoordinationDefault.refetch();
      } else {
        isFirstDistrictChange.current = false;
      }
    }
  }, [watchCustomerDistrictCode]);

  const isFirstWardChange = useRef(true);
  useEffect(() => {
    if (watchCustomerWardCode?.value) {
      if (!isFirstWardChange.current) {
        apiCalls.runnerCoordinationDefault.refetch();
      } else {
        isFirstWardChange.current = false;
      }
    }
  }, [watchCustomerWardCode]);

  const isFirstActionDateChange = useRef(true);
  useEffect(() => {
    if (watchActionDate) {
      if (!isFirstActionDateChange.current) {
        apiCalls.runnerCoordinationDefault.refetch();
      } else {
        isFirstActionDateChange.current = false;
      }
    }
  }, [watchActionDate]);

  useEffect(effects.handleHubChange, [hubId]);
  useEffect(effects.handleCustomerInfoChange, [watch()]);
  useEffect(effects.handleWardReset, [
    watchIsResetWard,
    watchCustomerProvinceCode,
  ]);

  // Handle runner coordination default effect
  useEffect(() => {
    if (apiCalls.runnerCoordinationDefault.data) {
    }
  }, [apiCalls.runnerCoordinationDefault.data]);

  // Map default address data when defaultData and province list are available
  useEffect(() => {
    if (
      defaultData &&
      provinceList?.length > 0 &&
      !addressMappingState.isProvinceSet
    ) {
      if (defaultData?.province_code) {
        const provinceOption = provinceList.find(
          p => p.value === defaultData.province_code
        );
        if (provinceOption) {
          setValue("customerProvinceCode", provinceOption, {
            shouldValidate: true,
          });
          setAddressMappingState(prev => ({ ...prev, isProvinceSet: true }));

          if (isAddressLevel2) {
            // DON'T reset ward state here - let it be handled by the ward mapping effect
            apiCalls.listLocality.ward.mutate({
              countryId: 1,
              type: 2,
              parentCode: defaultData.province_code,
              record_status: "O",
            });
          } else {
            apiCalls.listLocality.district.mutate({
              countryId: 1,
              type: 2,
              parentCode: defaultData.province_code,
              record_status: "C",
            });
          }
        }
      }
    }
  }, [
    defaultData,
    provinceList,
    addressMappingState.isProvinceSet,
    isAddressLevel2,
  ]);

  // Map district when district list is available (3 cấp only)
  useEffect(() => {
    if (
      defaultData &&
      !isAddressLevel2 &&
      districtList?.length > 0 &&
      !addressMappingState.isDistrictSet &&
      defaultData?.district_code
    ) {
      const districtOption = districtList.find(
        d => d.value === defaultData.district_code
      );
      if (districtOption) {
        setValue("customerDistrictCode", districtOption, {
          shouldValidate: true,
        });
        setAddressMappingState(prev => ({ ...prev, isDistrictSet: true }));

        // Fetch wards từ district
        apiCalls.listLocality.ward.mutate({
          countryId: 1,
          type: 3,
          parentCode: defaultData.district_code,
          record_status: "C",
        });
      }
    }
  }, [
    defaultData,
    districtList,
    addressMappingState.isDistrictSet,
    isAddressLevel2,
  ]);

  useEffect(() => {
    const hasDefaultData = !!defaultData;
    const hasWardList = wardList?.length > 0;
    const isWardNotSet = !addressMappingState.isWardSet;

    const hasWardCode = isAddressLevel2
      ? !!defaultData?.district_code
      : !!defaultData?.ward_code;

    if (hasDefaultData && hasWardList && isWardNotSet && hasWardCode) {
      let wardOption = null;

      if (isAddressLevel2) {
        const targetCode = defaultData.district_code;

        wardOption = wardList.find(
          w => w.value === targetCode || String(w.value) === String(targetCode)
        );

        if (!wardOption) {
          wardOption = wardList.find(w => {
            const wValue = String(w.value).toLowerCase();
            const targetValue = String(targetCode).toLowerCase();
            return wValue.includes(targetValue) || targetValue.includes(wValue);
          });
        }
      } else {
        const targetCode = defaultData.ward_code;

        wardOption = wardList.find(
          w => w.value === targetCode || String(w.value) === String(targetCode)
        );
      }

      if (wardOption) {
        setValue("customerWardCode", wardOption, {
          shouldValidate: true,
        });
        setAddressMappingState(prev => ({ ...prev, isWardSet: true }));
      } else {
      }
    }
  }, [defaultData, wardList, addressMappingState.isWardSet, isAddressLevel2]);

  // Reset address mapping state when defaultData changes
  useEffect(() => {
    if (defaultData) {
      setAddressMappingState({
        isProvinceSet: false,
        isDistrictSet: false,
        isWardSet: false,
      });
    }
  }, [
    defaultData?.province_code,
    defaultData?.district_code,
    defaultData?.ward_code,
  ]);

  // Map basic customer information
  useEffect(() => {
    if (defaultData) {
      setValue("customerAddress", defaultData?.full_address || "", {
        shouldValidate: true,
      });
      setValue("customerContactName", defaultData?.contact_name || "", {
        shouldValidate: true,
      });
      setValue("customerContactPhone", defaultData?.contact_phone || "", {
        shouldValidate: true,
      });
      setValue("note", defaultData?.note || "", {
        shouldValidate: true,
      });
    }
  }, [defaultData]);
  // Menu options object
  const menuOptions = {
    runner: useMemo(() => {
      if (hubId) {
        if (apiCalls.runners.data && apiCalls.runners.data.length > 0) {
          return apiCalls.runners.data.map(item => ({
            label: item?.display_name,
            value: item,
          }));
        }
        return [];
      }
      if (runners && runners.length > 0) {
        return runners.map(item => ({
          label: item?.display_name,
          value: item,
        }));
      }
      return [];
    }, [hubId, runners, apiCalls.runners.data]),
    timeSlot: timeSlots?.map(item => ({
      label: item?.name,
      value: item,
    })),
  };

  const [timeSlotMenu, setTimeSlotMenu] = useState([]);

  // useEffect(() => {
  //   setTimeSlotMenu(menuOptions.timeSlot);
  // }, [timeSlots]);

  // useEffect(() => {
  //   if (defaultData) {
  //     setValue("customerAddress", defaultData?.full_address || "", {
  //       shouldValidate: true,
  //     });
  //     setValue("customerContactName", defaultData?.contact_name || "", {
  //       shouldValidate: true,
  //     });
  //     setValue("customerContactPhone", defaultData?.contact_phone || "", {
  //       shouldValidate: true,
  //     });
  //     setValue("note", defaultData?.note || "", {
  //       shouldValidate: true,
  //     });
  //   }
  // }, [defaultData]);

  // Map timeslot and runner when data is available
  useEffect(() => {
    if (defaultData) {
      if (
        timeSlotMenu &&
        timeSlotMenu?.find(t => t?.value?.id === defaultData?.timeslot_id) &&
        !defaultStates.isTimeSlotDefaultSet
      ) {
        setValue(
          "timeslot_id",
          timeSlotMenu?.find(t => t?.value?.id === defaultData?.timeslot_id) ||
            null,
          { shouldValidate: true }
        );
        setDefaultStates(prev => ({ ...prev, isTimeSlotDefaultSet: true }));
      }
      if (
        menuOptions.runner &&
        menuOptions.runner?.find(
          t => t?.value?.id === defaultData?.runner_id
        ) &&
        !defaultStates.isRunnerDefaultSet
      ) {
        setValue(
          "runner_id",
          menuOptions.runner?.find(
            t => t?.value?.id === defaultData?.runner_id
          ) || null,
          { shouldValidate: true }
        );
        setDefaultStates(prev => ({ ...prev, isRunnerDefaultSet: true }));
      }
    }
  }, [defaultData, timeSlotMenu, menuOptions.runner]);
  // // START GET holidays
  // const [holidaysParams, setHolidaysParams] = useState({
  //   params: {
  //     columnFilter: {
  //       "filter[hub_id]": undefined,
  //     },
  //     sizePerPage: 10,
  //     page: -1,
  //   },
  //   run: false,
  //   useToDisalbeCalendar: true,
  //   queryKey: "holiday-list-order-coordination-with-time",
  // });

  // const {
  //   data: holidays,
  //   isFetching: isLoadingList,
  //   refetch: fetchHolidays,
  //   remove: removeHolidays,
  // } = useListHolidayWithTime(holidaysParams);

  useEffect(() => {
    setTimeSlotMenu(menuOptions.timeSlot);
  }, [timeSlots]);

  useEffect(() => {
    if (holidaysParams && holidaysParams?.run) {
      fetchHolidays();
    }
  }, [holidaysParams]);
  // END GET holidays

  useEffect(() => {
    if (!!datePickerState.actionDate && !!timeSlots) {
      const timeSlot = getTimeSlot({
        timeSlotOptions: menuOptions.timeSlot,
        date: datePickerState.actionDate,
        holidays: holidays?.items,
        isGrab: false,
        useTimeSlotRule: USE_TIME_SLOT_RULE,
        isHCMOrder: true,
        isCheckHolidayAndNotValid: false,
      });

      setTimeSlotMenu(timeSlot);
    }
  }, [datePickerState.actionDate, timeSlots, holidays]);

  useEffect(() => {
    setTimeSlotMenu(menuOptions.timeSlot);
  }, [timeSlots]);

  // useEffect(() => {
  //   if (defaultAddress.provinceCode) {
  //     if (provinceList?.length > 0) {
  //       const foundProvince = provinceList?.find(
  //         item => item.value === defaultAddress.provinceCode
  //       );
  //       if (foundProvince) {
  //         console.log("🚀 ~ Thành phố default:", foundProvince);
  //         setValue("customerProvinceCode", foundProvince);
  //         apiCalls.listLocality.district.mutate({
  //           countryId: 1,
  //           type: 2,
  //           parentCode: defaultAddress.provinceCode,
  //         });
  //       }
  //     }
  //   }
  // }, [JSON.stringify(provinceList), defaultAddress]);

  // useEffect(() => {
  //   // console.log("🚀 ~ useEffect ~ defaultAddress:", defaultAddress);
  //   // console.log("🚀 ~ useEffect ~ districtList:", districtList);
  //   if (defaultAddress.districtCode) {
  //     if (districtList?.length > 0) {
  //       const foundDistrict = districtList?.find(
  //         item => item.value === defaultAddress.districtCode
  //       );
  //       if (foundDistrict) {
  //         console.log("🚀 ~ Quận default:", foundDistrict);
  //         setValue("customerDistrictCode", foundDistrict);
  //         apiCalls.listLocality.ward.mutate({
  //           countryId: 1,
  //           type: 3,
  //           parentCode: defaultAddress.districtCode,
  //         });
  //       }
  //     }
  //   }
  // }, [JSON.stringify(districtList), defaultAddress]);

  // useEffect(() => {
  //   if (defaultAddress.wardCode) {
  //     if (wardList?.length > 0) {
  //       const foundWard = wardList?.find(
  //         item => item.value === defaultAddress.wardCode
  //       );
  //       if (foundWard) {
  //         console.log("🚀 ~ Phường default:", foundWard);
  //         setValue("customerWardCode", foundWard);
  //         setDefaultAddress(prev => ({
  //           ...prev,
  //           provinceCode: null,
  //           districtCode: null,
  //           wardCode: null,
  //         }));
  //       }
  //     }
  //   }
  // }, [JSON.stringify(wardList), defaultAddress]);

  return (
    <>
      <ModalBody className="OrderCoordinationForm">
        <Row xs="1">
          <Col>
            <Row xs="1" lg="2">
              <Col>
                <Label htmlFor="action_date">
                  {t("form.action_date")} <span className="text-danger">*</span>
                </Label>
                <FormGroup className="action_date_input">
                  <Controller
                    control={control}
                    name="action_date"
                    render={({ field }) => (
                      <SingleDatePicker
                        {...field}
                        date={datePickerState.actionDate}
                        onDateChange={handlers.changeDatePicker}
                        focused={datePickerState.focusedInput}
                        onFocusChange={handlers.toggleFocusedInput}
                        displayFormat="DD/MM/YYYY"
                        numberOfMonths={1}
                        small
                        placeholder=""
                        showDefaultInputIcon
                        inputIconPosition="after"
                        readOnly
                      />
                    )}
                  />
                  {errors.action_date && (
                    <MessageFormValidate error={errors.action_date.message} />
                  )}
                </FormGroup>
              </Col>
              <Col>
                <FormGroup>
                  <Label htmlFor="timeslot_id">
                    {t("form.timeslot_id")}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    control={control}
                    name="timeslot_id"
                    render={({ field }) => (
                      <Select
                        {...field}
                        isDisabled={timeSlotMenu?.length === 0}
                        placeholder={t("placeholder.select", {
                          title: t("form.timeslot_id"),
                        })}
                        className="basic-single w-100"
                        classNamePrefix="select"
                        options={timeSlotMenu}
                        onChange={handlers.changeTimeSlot}
                      />
                    )}
                  />
                  {errors.timeslot_id && (
                    <MessageFormValidate error={errors.timeslot_id.message} />
                  )}
                </FormGroup>
              </Col>
            </Row>
          </Col>

          <Col>
            <FormSelect
              mainForm={mainForm}
              fieldName="runner_id"
              isDisabled={
                menuOptions.runner?.length === 0 ||
                apiCalls.runnerCoordinationDefault.isLoading
              }
              dataList={menuOptions.runner}
              t={t}
              onChange={handlers.changeRunner}
              title={t("form.runner_id")}
              isRequired
            />
          </Col>

          <Col>
            <FormInput
              mainForm={mainForm}
              fieldName="note"
              type="textarea"
              t={t}
              title={t("placeholder.note")}
              rows={5}
              placeholder={t("placeholder.note")}
            />
          </Col>

          <Col>
            <Row xs="1" lg="2">
              <Col>
                <FormSelect
                  mainForm={mainForm}
                  fieldName="customerProvinceCode"
                  dataList={provinceList}
                  t={t}
                  title={t("form.receiver_province_code")}
                  isRequired={isRequireCustomerInfo}
                  isDisabled={apiCalls.listLocality.province.isLoading}
                  onChange={selectedProvince => {
                    setValue("customerDistrictCode", null);
                    setValue("customerWardCode", null);

                    setAddressMappingState(prev => ({
                      ...prev,
                      isWardSet: false,
                    }));

                    if (selectedProvince) {
                      if (isAddressLevel2) {
                        apiCalls.listLocality.ward.mutate({
                          countryId: 1,
                          type: 2,
                          parentCode: selectedProvince.value,
                          record_status: "O",
                        });
                      } else {
                        apiCalls.listLocality.district.mutate({
                          countryId: 1,
                          type: 2,
                          parentCode: selectedProvince.value,
                          record_status: "C",
                        });
                      }
                    } else {
                      apiCalls.listLocality.district.reset();
                      apiCalls.listLocality.ward.reset();
                    }
                  }}
                />
              </Col>
              {!isAddressLevel2 && (
                <Col>
                  <FormSelect
                    mainForm={mainForm}
                    fieldName="customerDistrictCode"
                    dataList={districtList}
                    t={t}
                    title={t("form.receiver_district_code")}
                    isRequired={isRequireCustomerInfo}
                    isDisabled={
                      !getValues("customerProvinceCode") ||
                      apiCalls.listLocality.district.isLoading
                    }
                    onChange={selectedDistrict => {
                      setValue("customerWardCode", null);
                      if (selectedDistrict) {
                        apiCalls.listLocality.ward.mutate({
                          countryId: 1,
                          type: 3,
                          parentCode: selectedDistrict.value,
                          record_status: "C",
                        });
                      } else {
                        apiCalls.listLocality.ward.reset();
                      }
                    }}
                  />
                </Col>
              )}
              <Col>
                <FormSelect
                  mainForm={mainForm}
                  fieldName="customerWardCode"
                  dataList={wardList}
                  t={t}
                  title={
                    isAddressLevel2 ? "Phường/Xã" : t("form.receiver_ward_code")
                  }
                  isRequired={isRequireCustomerInfo}
                  isDisabled={
                    !getValues("customerProvinceCode") ||
                    (!isAddressLevel2 && !getValues("customerDistrictCode")) ||
                    (isAddressLevel2
                      ? apiCalls.listLocality.ward.isLoading
                      : apiCalls.listLocality.ward.isLoading)
                  }
                />
              </Col>
              <Col>
                <FormInput
                  mainForm={mainForm}
                  fieldName="customerAddress"
                  type="text"
                  t={t}
                  title={t("form.receiver_address")}
                  isRequired={isRequireCustomerInfo}
                  placeholder={t("placeholder.enter", {
                    title: t("form.receiver_address"),
                  })}
                />
              </Col>
              <Col>
                <FormInput
                  mainForm={mainForm}
                  fieldName="customerContactName"
                  type="text"
                  t={t}
                  title={t("form.receiver_contact_name")}
                  isRequired={isRequireCustomerInfo}
                  placeholder={t("placeholder.enter", {
                    title: t("form.receiver_contact_name"),
                  })}
                />
              </Col>
              <Col>
                <FormInput
                  mainForm={mainForm}
                  fieldName="customerContactPhone"
                  type="number"
                  t={t}
                  title={t("form.receiver_contact_phone")}
                  isRequired={isRequireCustomerInfo}
                  placeholder={t("placeholder.enter", {
                    title: t("form.receiver_contact_phone"),
                  })}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter className="d-block">
        <Row>
          <Col>
            <Button
              color="danger"
              outline
              className="border-0"
              size="xs"
              onClick={() => handleCancel()}
            >
              {t("button.cancel")}
            </Button>
          </Col>
          <Col className="text-end">
            <Button
              size="xs"
              color="primary"
              style={{ backgroundColor: "#3C64B1" }}
              onClick={() => handleSubmit(handlers.submit)()}
            >
              {t("button.confirm")}
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </>
  );
};

OrderCoordinationForm.propTypes = {
  defaultData: PropTypes.any,
  timeSlots: PropTypes.array,
  runners: PropTypes.array,
  handleCancel: PropTypes.func,
  onActionDateChange: PropTypes.func,
  onTimeSlotChange: PropTypes.func,
  onForwardOrder: PropTypes.func,
  isMultipleOrders: PropTypes.bool,
};

OrderCoordinationForm.defaultProps = {
  defaultData: null,
  timeSlots: [],
  runners: [],
  handleCancel: () => {},
  onActionDateChange: () => {},
  onTimeSlotChange: () => {},
  onForwardOrder: () => {},
  isMultipleOrders: false,
};

export default OrderCoordinationForm;
